// Copyright (c) Laserfiche.
// Licensed under the MIT License. See LICENSE in the project root for license information.
var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { formatString } from './string-utils.js';
import { getLfLanguageCookie } from './cookie-utils.js';
const ResourceNotFoundError_NAME = 'ResourceNotFoundError';
class ResourceNotFoundError extends Error {
  constructor(message) {
    super(message);
    this.name = ResourceNotFoundError_NAME;
  }
}
export class LfLocalizationService {
  /**
   *
   * @example
   * ```typescript
   * const resource = new Map([
   *  ['jp-JP', { "LOADING": "読み込み中..." }],
   *  ['en-US', { "LOADING": "Loading..." }]  // have to provide 'en' in map
   * ]);
   * const localizationService = new LfLocalizationService(resource);
   *
   * // or
   *
   * const localizationService = new LfLocalizationService();
   * // have to call initResourcesFromUrlAsync later to load resources dynamically
   * ```
   */
  constructor(resources) {
    var _a, _b, _c, _d;
    /**
     * When true, returns pseudo language string. Defaults to false.
     * @example
     * ```typescript
     * const resource = new Map([
     *  ['jp-JP', { "LOADING": "読み込み中..." }],
     *  ['en-US', { "LOADING": "Loading..." }]
     * ]);
     * localizationService = new LfLocalizationService(resource);
     * localizationService.debugMode = true;
     * localizationService.getString('LOADING'); // '_ŀǿȧḓīƞɠ..._'
     * ```
     */
    this.debugMode = false;
    this.DEFAULT_LANGUAGE = 'en-US';
    this._resources = new Map();
    this._selectedLanguage = this.DEFAULT_LANGUAGE;
    this.ACCENTED_MAP = {
      a: 'ȧ',
      A: 'Ȧ',
      b: 'ƀ',
      B: 'Ɓ',
      c: 'ƈ',
      C: 'Ƈ',
      d: 'ḓ',
      D: 'Ḓ',
      e: 'ḗ',
      E: 'Ḗ',
      f: 'ƒ',
      F: 'Ƒ',
      g: 'ɠ',
      G: 'Ɠ',
      h: 'ħ',
      H: 'Ħ',
      i: 'ī',
      I: 'Ī',
      j: 'ĵ',
      J: 'Ĵ',
      k: 'ķ',
      K: 'Ķ',
      l: 'ŀ',
      L: 'Ŀ',
      m: 'ḿ',
      M: 'Ḿ',
      n: 'ƞ',
      N: 'Ƞ',
      o: 'ǿ',
      O: 'Ǿ',
      p: 'ƥ',
      P: 'Ƥ',
      q: 'ɋ',
      Q: 'Ɋ',
      r: 'ř',
      R: 'Ř',
      s: 'ş',
      S: 'Ş',
      t: 'ŧ',
      T: 'Ŧ',
      v: 'ṽ',
      V: 'Ṽ',
      u: 'ŭ',
      U: 'Ŭ',
      w: 'ẇ',
      W: 'Ẇ',
      x: 'ẋ',
      X: 'Ẋ',
      y: 'ẏ',
      Y: 'Ẏ',
      z: 'ẑ',
      Z: 'Ẑ'
    };
    if (resources) {
      if (!resources.get(this.DEFAULT_LANGUAGE)) {
        throw new ResourceNotFoundError(`Required language resource ${this.DEFAULT_LANGUAGE} is not found in provided map.`);
      }
      this._resources = resources;
    } else {
      console.log('No static resources provided. Call initResourcesFromUrlAsync to load resources dynamically.');
    }
    try {
      const domainCookie = (_a = document === null || document === void 0 ? void 0 : document.cookie) !== null && _a !== void 0 ? _a : '';
      const cookieLanguage = getLfLanguageCookie(domainCookie);
      const language = (_d = (_b = cookieLanguage === null || cookieLanguage === void 0 ? void 0 : cookieLanguage.uic) !== null && _b !== void 0 ? _b : (_c = window === null || window === void 0 ? void 0 : window.navigator) === null || _c === void 0 ? void 0 : _c.language) !== null && _d !== void 0 ? _d : this.DEFAULT_LANGUAGE;
      this.setLanguage(language);
    } catch (_e) {
      this.setLanguage(this.DEFAULT_LANGUAGE);
    }
  }
  /**
   * Resets the resource map to include remote language resource files: en by default, and
   * the closest selected language
   * (e.g.: if selected language is fr-CA, and fr-CA doesn't exists but fr exists, it loads fr)
   *
   * @param url - the url to the language file's folder
   * @example
   * ```typescript
   * const localizationService = new LfLocalizationService();
   * const resourcesFolder = 'https://lfxstatic.com/npm/@laserfiche/lf-resource-library@5/resources/laserfiche-base';
   * localizationService.setLanguage('fr-CA');
   * await localizationService.initResourcesFromUrlAsync(resourcesFolder); // loads en-US.json and fr-FR.json
   * ```
   */
  initResourcesFromUrlAsync(url) {
    return __awaiter(this, void 0, void 0, function* () {
      this._resources = new Map();
      this._currentResource = undefined;
      url = this.terminateUrlWithSlash(url);
      yield this.getDefaultLanguageResourceAsync(url);
      yield this.getSelectedLanguageResourceAsync(url);
    });
  }
  /**
   * Loads the selected language resource given the url pointing to the folder of the resource,
   * if HTTP receives 403 or 404 error, loads the non-region-specific language resource,
   * throws error otherwise
   * @param url - url pointing to folder of the resource
   */
  getSelectedLanguageResourceAsync(url) {
    return __awaiter(this, void 0, void 0, function* () {
      try {
        yield this.trySetLanguageResourceAsync(url, this._selectedLanguage);
        return;
      } catch (e) {
        console.warn(`Selected language resource ${this._selectedLanguage} is not found at ${url}${this._selectedLanguage}.json.`);
        if ((e === null || e === void 0 ? void 0 : e.name) === ResourceNotFoundError_NAME) {
          const closestLanguage = this.mapToClosestLanguage(this._selectedLanguage);
          try {
            yield this.trySetLanguageResourceAsync(url, closestLanguage);
            return;
          } catch (_a) {
            console.warn(`Language resource ${closestLanguage} is not found at ${url}${closestLanguage}.json.`);
          }
        } else {
          console.error(e);
        }
      }
      this.setLanguageResource(this.DEFAULT_LANGUAGE);
    });
  }
  trySetLanguageResourceAsync(url, language) {
    return __awaiter(this, void 0, void 0, function* () {
      if (!this.setLanguageResource(language)) {
        yield this.addResourceFromUrlAsync(`${url}${language}.json`, language);
        this.setLanguageResource(language);
        console.log(`Loaded resource from ${url}${language}.json.`);
      }
    });
  }
  /**
   * Loads the default langauge resource given the url pointing to the folder of the resource,
   * throws error if resource not found
   * @param url - url pointing to the folder of the resource
   */
  getDefaultLanguageResourceAsync(url) {
    return __awaiter(this, void 0, void 0, function* () {
      try {
        yield this.addResourceFromUrlAsync(`${url}${this.DEFAULT_LANGUAGE}.json`, this.DEFAULT_LANGUAGE);
      } catch (e) {
        if ((e === null || e === void 0 ? void 0 : e.name) === ResourceNotFoundError_NAME) {
          throw new ResourceNotFoundError(`Required language resource ${this.DEFAULT_LANGUAGE} is not found in ${url}${this.DEFAULT_LANGUAGE}.json.`);
        } else {
          throw e;
        }
      }
    });
  }
  /**
   * Sets currentResource given the language code, fall back to available resource if necessary.
   * @example
   * ```typescript
   * const localizationService = new LfLocalizationService();
   * const resourcesFolder = 'https://lfxstatic.com/npm/@laserfiche/lf-resource-library@5/resources/laserfiche-base';
   * localizationService.setLanguage('fr-CA');  // gives a warning since no resource exists at this point
   * await localizationService.initResourcesFromUrlAsync(resourcesFolder); // loads en.json and fr.json because language has been set to be fr-CA
   * localizationService.currentLanguage // {'language': 'fr', 'resource': { ... }}
   * ```
   * @example
   * ```typescript
   * const resource = new Map([
   *  ['jp-JP', { "LOADING": "読み込み中..." }],
   *  ['en-US', { "LOADING": "Loading..." }]
   * ]);
   * localizationService = new LfLocalizationService(resource);
   * localizationService.setLanguage('jp-JP');  // set currentResource to ir
   * localizationService.currentLanguage // {'language': 'jp-JP', 'resource':{ "LOADING": "読み込み中..." }}
   * ```
   */
  setLanguage(language) {
    this._selectedLanguage = language;
    this.setResourceWithFallBack(language);
  }
  /**
   * Returns the current language resource in use
   * @example
   * ```typescript
   * const resource = new Map([
   *  ['jp', { "LOADING": "読み込み中..." }],
   *  ['en', { "LOADING": "Loading..." }]
   * ]);
   * localizationService = new LfLocalizationService(resource);
   * localizationService.currentResource // {'language': 'en', 'resource':{ "LOADING": "Loading..." }}
   * ```
   */
  get currentResource() {
    return this._currentResource;
  }
  /**
   * Returns the translated formatted string if exists,
   * falls back to default resource if translated string doesn't exist in current resource
   * throws an error if current resource does not exist
   * @param key - the string to translate
   * @param params - the tokens to replace in translated string if exist
   * @returns the translated string with tokens
   * @example
   * ```typescript
   * const resource = new Map([
   *  ['jp-JP', { "LOADING": "読み込み中..." }],
   *  ['en-US', { "LOADING": "Loading..." }]
   * ]);
   * localizationService = new LfLocalizationService(resource);
   * localizationService.getString('LOADING'); // 'Loading...'
   * localizationService.setLanguage('jp-JP');
   * localizationService.getString('LOADING'); // '読み込み中...'
   * ```
   */
  getString(key, params) {
    var _a, _b;
    if (!this._currentResource) {
      console.warn('Current resource not found.');
      return `<< ${key} >>`;
    }
    let localizedString = (_a = this._currentResource) === null || _a === void 0 ? void 0 : _a.resource[key];
    if (!localizedString) {
      const defaultResource = this._resources.get(this.DEFAULT_LANGUAGE);
      if (defaultResource) {
        localizedString = defaultResource[key];
      }
      if (localizedString) {
        console.warn(`Resource '${key}' not found in ${(_b = this._currentResource) === null || _b === void 0 ? void 0 : _b.language}.
        Falling back to ${this.DEFAULT_LANGUAGE}.`);
      } else {
        console.warn(`Resource '${key}' not found. Use initResourcesFromUrlAsync to load resource.`);
        return `<< ${key} >>`;
      }
    }
    try {
      const formattedString = formatString(localizedString, params);
      return this.convertToPseudoLanguage(formattedString);
    } catch (_c) {
      console.warn(`Given arguments for ${key} did not match required number of arguments.`);
      return this.convertToPseudoLanguage(localizedString);
    }
  }
  /**
   * Fetchs the remote json file and add it to this._resources
   * @param url - the remote url to the resource file
   * @param code - format languagecode2-country/regioncode2
   */
  addResourceFromUrlAsync(url, code) {
    return __awaiter(this, void 0, void 0, function* () {
      const response = yield fetch(url);
      if (response.status > 399 && response.status < 500) {
        throw new ResourceNotFoundError(`HTTP error ${response.status} at ${url}`);
      } else if (response.status === 200) {
        const json = yield response.json();
        this._resources.set(code, json);
        return json;
      } else {
        throw new Error(`addResourceFromUrlAsync(${url}) HTTP status ${response.status}.`);
      }
    });
  }
  /**
   * Sets _currentResource based on given language, if language exists in _resource,
   * else if language does not exist in _resource, set to the language without dash if exists in _resource,
   * else if language without dash doesn't exist, set to default language which is guaranteed to exist.
   * @param language - language abbreviation
   * @returns
   */
  setResourceWithFallBack(language) {
    const setCurrentResourceSuccess = this.setLanguageResource(language);
    if (setCurrentResourceSuccess) return;
    console.warn(`Language resource ${language} is not found.`);
    const closestLanguage = this.mapToClosestLanguage(language);
    if (closestLanguage != language) {
      const setClosestLanguageResourceFallBackSuccess = this.setLanguageResource(closestLanguage);
      if (setClosestLanguageResourceFallBackSuccess) return;
      console.warn(`Language resource ${closestLanguage} is not found.`);
    }
    const setCurrentResourceFallBackDefaultSuccess = this.setLanguageResource(this.DEFAULT_LANGUAGE);
    if (setCurrentResourceFallBackDefaultSuccess) {
      console.warn(`Use initResourcesFromUrlAsync to load resource.
    Fall back to use default language ${this.DEFAULT_LANGUAGE}.`);
    } else {
      console.warn('Resource is not found. Cannot set currentResource.');
      this._currentResource = undefined;
    }
  }
  /**
   * Sets _currentResource based on given language
   * @param language - language abbreviation
   * @returns true if _currentResource is set, false if language doesn't exist in _resource
   */
  setLanguageResource(language) {
    const resource = this._resources.get(language);
    if (resource) {
      this._currentResource = {
        language: language,
        resource: resource
      };
      return true;
    }
    return false;
  }
  mapToClosestLanguage(originalLanguage) {
    const languageWithoutDash = this._selectedLanguage.split('-')[0];
    switch (languageWithoutDash) {
      case 'zh':
        switch (originalLanguage) {
          case 'zh-CN':
            return 'zh-Hans';
          case 'zh-TW':
            return 'zh-Hant';
          case 'zh-HK':
            return 'zh-Hant';
          default:
            return 'zh-Hans';
        }
      case 'ar':
        return 'ar-EG';
      case 'en':
        return 'en-US';
      case 'es':
        return 'es-MX';
      case 'fr':
        return 'fr-CA';
      case 'it':
        return 'it-IT';
      case 'pt':
        return 'pt-BR';
      case 'th':
        return 'th-TH';
      case 'cs':
        return 'cs-CZ';
      case 'de':
        return 'de-DE';
      case 'el':
        return 'el-GR';
      case 'id':
        return 'id-ID';
      case 'ja':
        return 'ja-JP';
      case 'ko':
        return 'ko-KR';
      case 'ms':
        return 'ms-MY';
      case 'nl':
        return 'nl-NL';
      case 'ro':
        return 'ro-RO';
      case 'sr':
        return 'sr-Latn';
      case 'tr':
        return 'tr-TR';
      case 'vi':
        return 'vi-VN';
      default:
        return originalLanguage;
    }
  }
  /**
   * Ensures the url ends with '/'
   * @param url - url to process
   * @returns path with '/'
   */
  terminateUrlWithSlash(url) {
    if (!url.endsWith('/')) {
      url = url.concat('/');
    }
    return url;
  }
  convertToPseudoLanguage(value) {
    if (!this.debugMode) {
      return value;
    }
    let pseudoLocalizedText = '_';
    for (const character of value) {
      if (this.ACCENTED_MAP[character]) {
        pseudoLocalizedText += this.ACCENTED_MAP[character];
      } else {
        pseudoLocalizedText += character;
      }
    }
    return pseudoLocalizedText + '_';
  }
}
